import React from 'react';
import styled from 'styled-components';

import Imgix from 'shared/components/imgix';
import useTranslation from 'hooks/use-translation';

import { palette } from './palette';
import { Container, DrawLink, GoldButton, RevealOnScroll, Section, SectionHead } from './primitives';

type ShopLocalSectionProps = {
  onCtaClick: () => void;
};

const DISPENSARIES = [
  {
    name: `Northeast Alternatives`,
    logo: `https://images.dutchie.com/home-redesign/dispensary-northeast.png`,
    meta: `Pickup available · 7 Miles away`,
    ready: `READY IN 10-20 MINS`,
  },
  {
    name: `Native Sun - Attleboro`,
    logo: `https://images.dutchie.com/home-redesign/dispensary-native-sun.png`,
    meta: `Pickup available · 7 Miles away`,
    ready: `READY IN 5-10 MINS`,
  },
  {
    name: `Solar Cannabis Co.`,
    logo: `https://images.dutchie.com/home-redesign/dispensary-solar.png`,
    meta: `Pickup available · 9 Miles away`,
    ready: `READY IN 5-10 MINS`,
  },
];

export function ShopLocalSection({ onCtaClick }: ShopLocalSectionProps): JSX.Element {
  const { t } = useTranslation();

  return (
    <Section $alt>
      <Container>
        <SectionHead
          first={t('home.redesign.shopLocalFirst', 'Shop')}
          rest={t('home.redesign.shopLocalRest', 'local')}
          sub={t(
            'home.redesign.shopLocalSub',
            'The highest-rated dispensaries in your area, with real-time menus and online ordering'
          )}
        />
        <RevealOnScroll>
          <Cards>
            {DISPENSARIES.map((dispensary) => (
              <Card key={dispensary.name}>
                <Logo>
                  <Imgix src={dispensary.logo} width={200} height={200} htmlAttributes={{ alt: dispensary.name }} />
                </Logo>
                <h3>{dispensary.name}</h3>
                <Meta>{dispensary.meta}</Meta>
                <Ready>{dispensary.ready}</Ready>
                <GoldButton type='button' onClick={onCtaClick}>
                  {t('home.redesign.orderNow', 'Order now')}
                </GoldButton>
              </Card>
            ))}
          </Cards>
        </RevealOnScroll>
        <SeeAll>
          <DrawLink as='button' type='button' onClick={onCtaClick}>
            {t('home.redesign.seeAllDispensaries', 'See all dispensaries near you')} <span className='arr'>→</span>
          </DrawLink>
        </SeeAll>
      </Container>
    </Section>
  );
}

const Cards = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;

  ${({ theme }) => theme.breakpoints.down(`xs`)} {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: ${palette.bgCard};
  border-radius: 16px;
  padding: 22px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  transition: transform 0.25s ease, box-shadow 0.25s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 18px 40px rgba(0, 0, 0, 0.35);
  }

  h3 {
    margin: 0;
    font-size: 22px;
    color: ${palette.cream};
  }

  button {
    width: 100%;
  }
`;

const Logo = styled.div`
  background: #ffffff;
  border-radius: 12px;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  img {
    max-width: 70%;
    max-height: 70%;
    object-fit: contain;
  }
`;

const Meta = styled.div`
  font-size: 14px;
  color: ${palette.creamDim};
`;

const Ready = styled.div`
  color: ${palette.gold};
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.5px;

  &::before {
    content: '⚡ ';
  }
`;

const SeeAll = styled.div`
  text-align: center;
  margin-top: 40px;

  button {
    background: none;
    border: none;
    font-size: 16px;
    font-family: inherit;
  }
`;
